<template>
	<!-- TODO: Check how to use stacked without massive space-between or table grid blow-out
		responsiveLayout="stack"
		breakpoint="1946px" -->
	<DataTable
		class="p-shadow-2 p-datatable-sm"
		:value="queueEntries"
		:lazy="true"
		:paginator="true"
		:loading="!loaded || lockTable"
		paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
		:rowsPerPageOptions="[25,50,100]"
		:rows="paginationInfo.pageSize"
		:totalRecords="paginationInfo.total"
		columnResizeMode="fit"
		responsiveLayout="scroll"
		:scrollable="true"
		scrollHeight="flex"
		v-model:selection="selectedRows"
		@row-click="onRowClick($event)"
		@page="onPage($event)"
		:currentPageReportTemplate="'{first} ' + $t('Squeeze.General.To') + ' {last} ' + $t('Squeeze.General.Of') + ' {totalRecords} '"
		:rowHover="true"
		:metaKeySelection="false"
	>
		<template #empty>
			{{$t('Squeeze.Queue.Filter.NoEntries')}}
		</template>

		<Column v-if="store.state.isAdminUser" headerClass="header-center" style="min-width: 2rem; max-width: 2rem; text-align: center;" selectionMode="multiple"></Column>

		<Column field="id" :header="$t('Squeeze.Queue.Fields.id')" style="flex: 0 0 4rem; padding: 0.6rem;"></Column>

		<Column field="status" :header="$t('Squeeze.Queue.Fields.status')" style="padding: 0.6rem; max-width: 10rem;">
			<template #body="slotProps">
				<div style="text-align: left!important; white-space:nowrap!important;">
					<span :class="'status-badge ' + slotProps.data.workflowContext.status.toLowerCase()" class="p-py-1">
						{{$t("Squeeze.Queue.States." + slotProps.data.workflowContext.status)}}
					</span>
				</div>
			</template>
		</Column>

		<Column field="errorText" :header="$t('Squeeze.General.Comment')" style="padding: 0.6rem; min-width: 4rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.workflowContext.errorText">
					{{slotProps.data.workflowContext.errorText}}
				</div>
			</template>
		</Column>

		<Column field="createdTs" :header="$t('Squeeze.WorkflowContext.createdTs')" style="padding: 0.6rem;">
			<template #body="slotProps">
				<div style="white-space:nowrap!important;">
					{{formatDate(slotProps.data.workflowContext.createdTs)}}
				</div>
			</template>
		</Column>

		<Column field="modifiedTs" :header="$t('Squeeze.WorkflowContext.modifiedTs')" style="padding: 0.6rem;">
			<template #body="slotProps">
				<div style="white-space:nowrap!important;">
					{{formatDate(slotProps.data.workflowContext.modifiedTs)}}
				</div>
			</template>
		</Column>

		<!-- Refresh / Delete -->
		<Column style="min-width: 7rem; max-width: 7rem; text-align: right;">
			<template #body="slotProps">
				<Button v-if="store.state.isAdminUser" v-tooltip="$t('Squeeze.Queue.Requeue.Requeue')" icon="mdi mdi-file-refresh-outline" class="p-button p-button-rounded p-button-success p-button-text" @click="openRequeueMenu($event, slotProps.data)" aria-haspopup="true" aria-controls="single_entry_menu" />
				<Menu id="single_entry_menu" ref="singleEntryMenu" :model="steps" :popup="true" />
				<Button v-tooltip="$t('Squeeze.Validation.Buttons.Delete')" icon="mdi mdi-delete-outline" class="p-button p-button-rounded p-button-danger p-button-text" disabled />
			</template>
		</Column>

		<template #paginatorstart>
			<Button type="button" v-tooltip="$t('Squeeze.General.Refresh')" icon="mdi mdi-refresh" class="p-button-text" @click="onReload" :disabled="!loaded" />
			<Button v-if="store.state.isAdminUser" type="button" icon="mdi mdi-file-refresh-outline" class="p-button-text" :label="$t('Squeeze.Queue.Requeue.Requeue')" @click="openMultiRequeueMenu" :disabled="!selectedRows.length" aria-haspopup="true" aria-controls="multi_entry_menu" />
			<Menu id="multi_entry_menu" ref="multiEntryMenu" :model="steps" :popup="true" />
			<!--Button type="button" icon="mdi mdi-folder-open" class="p-button-text" :label="$t('Squeeze.Queue.General.GoTo')" @click="openGoToMenu" aria-haspopup="true" aria-controls="multi_entry_menu_goto" />
			<Menu id="multi_entry_menu_goto" ref="multiEntryMenu" :model="steps" :popup="true" /-->
		</template>
		<template #paginatorend></template>
	</DataTable>
</template>

<script lang="ts">
import {defineComponent, onMounted, PropType, reactive, ref} from 'vue';
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import useSqueezeStore from "@/apps/squeeze/store";
import {getSqueezeStepIconMap} from "@/apps/squeeze/App.vue";
import {useConfirm} from "primevue/useconfirm";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Menu from "primevue/menu";
import {DocumentField, PaginationDto, QueueEntry} from "@dex/squeeze-client-ts";
import {ClientManager} from "@/singletons/ClientManager";
import {ToastManager} from "@/util/ToastManager";
import {formatDate as formatDateUtil, getLocaleCode} from "@/util/DateHelper";
import {ParsingHelper} from "@/util/ParsingHelper";
import {hasActiveTextSelection} from "@/util/TextSelectionHelper";
import {FilterMatchMode} from "primevue/api";
import {DEFAULT_LOCALE} from "@/lang";
import {i18n} from "@/main";
import Tooltip from "primevue/tooltip";

interface MenuItem {
	label: string;
	items: MenuSubItem[];
}

interface MenuSubItem {
	label: string;
	icon: string;
	command: {};
}

export default defineComponent({
	name: "QueueEntries",
	components: {
		DataTable,
		Column,
		Button,
		Menu,
	},
	props: {
		/** ID of document class */
		documentClassId: {
			type: Number,
			required: true,
		},
		/** Name of document class */
		documentClassName: {
			type: String,
			required: true,
		},
		/** List of all QueueEntries in QueueStep Validation */
		queueEntries: {
			type: Array as PropType<QueueEntry[]>,
			default: () => [],
		},
		/** Indicates end of request */
		loaded: {
			type: Boolean,
			default: false,
		},
		/** Pagination information */
		paginationInfo: {
			type: Object as PropType<PaginationDto>,
			default: () => ({}),
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: ["onRowSelect", "onReload", "onPage"],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const confirm = useConfirm();

		/** Current Vuex Store */
		const store = useSqueezeStore();

		const filters = reactive<any>({
			"id": {value: null, matchMode: FilterMatchMode.EQUALS},
			"create_ts": {value: null, matchMode: FilterMatchMode.CONTAINS},
			"status": {value: null, matchMode: FilterMatchMode.EQUALS},
			"errorText": {value: null, matchMode: FilterMatchMode.CONTAINS},
		});

		/** Requeue steps to choose in menu */
		const steps = ref<MenuItem[]>([]);

		/** Currently selected row */
		const selection = reactive<QueueEntry>({});

		/** All selection rows */
		const selectedRows = ref<QueueEntry[]>([]);

		/** Is table locked? */
		const lockTable = ref<boolean>(false);

		/** Current locale */
		const lang = ref(DEFAULT_LOCALE);

		/** Ref of single entry menu */
		const singleEntryMenu = ref(null);

		/** Ref of multi entry menu */
		const multiEntryMenu = ref(null);

		/** Queue API endpoint */
		const queueApi = ClientManager.getInstance().squeeze.queue;

		/**
		 * Triggered when a row is clicked
		 * @param event
		 */
		const onRowClick = (event: { originalEvent: MouseEvent; data: QueueEntry; index: number }) => {
			/* Prevent the row-click in the selection-column with the checkbox
			* Use the selectionMode 'multiple' only at the respective column and not at the table, when you also use the row-click event
			* If the selectionMode is set on the table and the row click event is active, these two events overwrite each other
			*/
			if (event.originalEvent
				&& !(event.originalEvent.target as HTMLElement).matches('.p-selection-column')
				&& !(event.originalEvent.target as HTMLElement).matches('.p-checkbox *')
				&& !hasActiveTextSelection()) {
				emit("onRowSelect", event.data);
			}
			return;
		}

		/**
		 * Triggered when the next page is selected
		 * @param event
		 */
		const onPage = (event: any) => {
			emit("onPage", event)
		}

		/** Triggered when table content should be reloaded */
		const onReload = () => {
			emit("onReload");
		}

		/**
		 * Opens requeue menu
		 * @param {MouseEvent} event
		 * @param {QueueEntry} data
		 */
		const openRequeueMenu = (event: MouseEvent, data: QueueEntry) => {
			Object.assign(selection, data);

			const singleMenu = singleEntryMenu.value as any;
			if (singleMenu) {
				singleMenu.toggle(event);
			}
		}

		/**
		* Opens the Requeue Menu
		* @param event
		*/
		const openMultiRequeueMenu = (event: any) => {
			if (!selectedRows.value.length) {
				return;
			}

			const multiMenu = multiEntryMenu.value as any;
			if (multiMenu) {
				multiMenu.toggle(event);
			}
		}

		/**
		* Opens the Requeue Menu
		* @param event
		*/
		const openGoToMenu = (event: any) => {
			const multiMenu = multiEntryMenu.value as any;
			if (multiMenu) {
				multiMenu.toggle(event);
			}
		}

		/**
		 * Requeue selected QueueEntry to specified step
		 * @param {string} step
		 */
		const requeueEntry = (step: string) => {
			if(selection && selection.id) {
				// REQUEUE API ENDPOINT
				lockTable.value = true;
				queueApi.requeueEntry(selection.id, step).then(() => {
					ToastManager.showSuccess(toast, t('Squeeze.Queue.Requeue.Success'), t('Squeeze.Queue.Steps.' + step));
				}).catch(reason => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), reason);
				}).finally(() => {
					Object.assign(selection, {});
					lockTable.value = false;
					onReload();
				})
			} else {
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Select.Entry.One'));
			}
		}

		/**
		 * Requeue the selected entries
		 * @param {string} step
		 */
		const requeueSelectedEntries = (step: string) => {
			if (!selectedRows.value || !selectedRows.value.length) {
				return;
			}

			lockTable.value = true;
			selectedRows.value.reduce((chain, { id }) => {
				if (id == undefined) {
					return chain;
				}
				return chain.finally(() => queueApi.requeueEntry(id, step).catch(() => { /* swallow errors */ }))
			}, Promise.resolve()).finally(() => {
				ToastManager.showSuccess(toast, t('Squeeze.Queue.Requeue.Success'), t('Squeeze.Queue.Steps.' + step));
				selectedRows.value = [];
				lockTable.value = false;
				onReload();
			});
		}

		/**
		 * Formats date string to desired locale string
		 * @param {string} dateToFormat
		 */
		const formatDate = (dateToFormat: string) => {
			lang.value = i18n.global.locale.value;
			return formatDateUtil(dateToFormat, lang.value);
		}

		/**
		 * Parses an amount (string) to locale string
		 * @param {string} value
		 */
		const parseAmounts = (value: string) => {
			return ParsingHelper.formatAmount(value, getLocaleCode(lang.value));
		}

		/**
		 * Returns field value of passed DocumentField array
		 * @param {DocumentField[]} fields
		 * @param {number} fieldId
		 * @param {string} type
		 */
		const getFieldValue = (fields: DocumentField[], fieldId: number, type: string) => {
			if (fields && fieldId) {
				const fieldValue = fields.find((field) => {
					return field.id === fieldId;
				});

				if (fieldValue != undefined) {
					if (fieldValue.value && fieldValue.value.value) {
						switch(type.toLowerCase()) {
						case "amount": return parseAmounts(fieldValue.value.value);
						case "date": return formatDate(fieldValue.value.value);
						default: return fieldValue.value.value;
						}
					}
				}
			}

			return "";
		}

		onMounted(() => {
			steps.value = [{
				label: t('Squeeze.Queue.General.Steps'),
				items: [],
			}];
			// Only build menu entries for steps the backend currently reports as available
			// (already respects feature flags/license); the local icon map is used as a
			// safeguard so unknown step names from the backend are never rendered
			store.state.queueSteps
				.filter((step: string) => getSqueezeStepIconMap().has(step))
				.forEach((step: string) => {
					const icon = getSqueezeStepIconMap().get(step) ?? '';
					steps.value[0].items.push({
						label: t('Squeeze.Queue.Steps.' + step),
						icon: icon,
						command: (ev: { item: any; originalEvent: any }) => {
							let messageText = '';
							let acceptFn = null;

							// get parent element of target
							const el = ev.originalEvent.target as HTMLElement;
							const closestParent = el.closest('div');

							if (closestParent && closestParent.matches('#multi_entry_menu')) {
								// If entry in multi_menu is clicked (Requeue-Button under table for multi-selection)
								messageText = t('Squeeze.Queue.Requeue.ForwardEntries');
								acceptFn = () => {
									requeueSelectedEntries(step);
								};
							}
							else if ((ev.originalEvent.target as HTMLElement).matches('#single_entry_menu *')) {
								// If entry in single_menu is clicked (Requeue-Button in every single row)
								messageText = t('Squeeze.Queue.Requeue.ForwardEntry');
								acceptFn = () => {
									requeueEntry(step);
								};
							}
							else {
								return;
							}

							confirm.require({
								message: messageText + ": " + t('Squeeze.Queue.Steps.' + step) + " ?",
								header: t('Squeeze.General.Confirmation'),
								icon: 'pi pi-exclamation-triangle',
								accept: acceptFn,
								reject: () => { /**/ },
							});
						},
					});
				});
		});

		return {
			t,
			toast,
			store,
			filters,
			steps,
			selection,
			selectedRows,
			lockTable,
			lang,
			singleEntryMenu,
			multiEntryMenu,
			onRowClick,
			onPage,
			onReload,
			openRequeueMenu,
			openMultiRequeueMenu,
			openGoToMenu,
			requeueEntry,
			requeueSelectedEntries,
			formatDate,
			parseAmounts,
			getFieldValue,
		};
	},
});

</script>

<style lang="scss" scoped>

// Table header
::v-deep(th) {
	background-color: var(--dex-primary-light-color) !important;
	color: var(--dex-text-color);
}

.status-badge {
	font-weight: bold;
	&.initial {
		border-color: var(--queuestate-initial-primary);
		color: var(--queuestate-initial-darker);
	}

	&.work {
		border-color: var(--queuestate-work-primary);
		color: var(--queuestate-work-darker);
	}

	&.waiting {
		border-color: var(--queuestate-waiting-primary);
		color: var(--queuestate-waiting-darker);
	}

	&.error {
		border-color: var(--queuestate-error-primary);
		color: var(--queuestate-error-darker);
	}

	&.suspend {
		border-color: var(--queuestate-suspend-primary);
		color: var(--queuestate-suspend-darker);
	}

	&.suspend-marked {
		border-color: var(--queuestate-suspend-darker);
		color: var(--queuestate-suspend-darker);
	}

	&.clarification {
		border-color: var(--queuestate-clarification-primary);
		color: var(--queuestate-clarification-darker);
	}

	&.finished {
		border-color: var(--queuestate-finished-primary);
		color: var(--queuestate-finished-darker);
	}
}

::v-deep(td div.column-cell) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

::v-deep(.header-center div.p-column-header-content) {
	justify-content: center;
}

</style>
